import * as React from "react"
import axios from "axios"
import { useAtom } from "jotai"
import { useQueryClient, type QueryClient } from "@tanstack/react-query"

import { api } from "@/api/instance"
import { type TaskOut } from "@/api/client"
import { apiBaseUrl } from "@/shared/api/apiBaseUrl"
import {
  getProfileMeta,
  patchProfileMeta,
  setProfileMeta,
} from "@/entities/profile"
import { settled } from "@/shared/lib/settled"
import {
  adoptRenamedProfile,
  canCommitProfileWrite,
} from "../adoptRenamedProfile"
import {
  isTourProcessingHold,
  noteTourHoldPendingName,
  noteTourHoldRenamed,
} from "@/features/demo-tour"
import { selectedProfileIdAtom } from "../store"
import { extractErrorMessage } from "../utils"
import {
  isNonEmptyProfileData,
  normalizeProfileData,
} from "../components/ProfileView"
import {
  TASK_POLL_INTERVAL_MS,
  classifyPollFailure,
  nextRetryDelay,
} from "../taskTracking"

function withTrailingSlash(url: string): string {
  return url.endsWith("/") ? url : `${url}/`
}

function commitIfTracking(
  live: boolean,
  qc: QueryClient,
  profileId: string,
  taskId: string,
  write: () => void,
): void {
  if (!canCommitProfileWrite(qc, profileId, { live, taskId })) return
  write()
}

/**
 * SSE + polling for one active task. Writes only via setQueryData helpers
 * and refuses commits after unmount or after the profile/task generation
 * has moved away (rename race).
 */
export function useTaskStream(profileId: string, taskId: string): void {
  const queryClient = useQueryClient()
  const [, setSelectedProfileId] = useAtom(selectedProfileIdAtom)

  // Unmount-only: патч completed сам снимает taskId и размонтирует подписчика,
  // поэтому guard, привязанный к cleanup эффекта, отбросил бы уже начатый getProfile.
  const mounted = React.useRef(true)
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  React.useEffect(() => {
    const live = mounted
    let pollFailures = 0
    let pollInterval: ReturnType<typeof setInterval> | undefined
    let streamClose: (() => void) | undefined

    const stop = () => {
      if (pollInterval !== undefined) {
        clearInterval(pollInterval)
        pollInterval = undefined
      }
      streamClose?.()
      streamClose = undefined
    }

    const loadProfileDataForProfile = async (targetId: string) => {
      if (!live.current) return
      if (getProfileMeta(queryClient, targetId) === undefined) return

      patchProfileMeta(queryClient, targetId, {
        isLoadingProfileData: true,
        errorMessage: null,
      })

      const result = await settled(api.v1.getProfile(targetId))
      // Completion clears taskId / sets success, which unmounts this subscriber
      // (RecsysSubscriptions). Still commit — otherwise isLoadingProfileData
      // sticks true and the hydrator refuses to load.
      if (getProfileMeta(queryClient, targetId) === undefined) return

      if (!result.ok) {
        const err = result.error
        const httpStatus = axios.isAxiosError(err)
          ? err.response?.status
          : undefined
        const isServerError = httpStatus === undefined || httpStatus >= 500
        const current = getProfileMeta(queryClient, targetId) ?? {}
        if (current.status === "processing") {
          setProfileMeta(queryClient, targetId, {
            ...current,
            hasLoadedProfileData: true,
            isLoadingProfileData: false,
          })
        } else {
          const preserveStatus =
            current.status === "error" || current.status === "success"
          setProfileMeta(queryClient, targetId, {
            ...current,
            ...(isServerError
              ? {
                  status: "error",
                  errorMessage:
                    "Не удалось загрузить анкету профиля. Обновите страницу.",
                }
              : preserveStatus
                ? {}
                : { status: "not_started" }),
            profileData: undefined,
            hasLoadedProfileData: true,
            isLoadingProfileData: false,
          })
        }
        return
      }

      const presentatorData = result.value.data as unknown

      if (!isNonEmptyProfileData(presentatorData)) {
        const current = getProfileMeta(queryClient, targetId) ?? {}
        const preserveStatus =
          current.status === "processing" ||
          current.status === "error" ||
          current.status === "success"
        setProfileMeta(queryClient, targetId, {
          ...current,
          ...(preserveStatus ? {} : { status: "not_started" }),
          profileData: undefined,
          hasLoadedProfileData: true,
          isLoadingProfileData: false,
        })
        return
      }

      const current = getProfileMeta(queryClient, targetId) ?? {}
      setProfileMeta(queryClient, targetId, {
        ...current,
        profileData: normalizeProfileData(presentatorData),
        hasLoadedProfileData: true,
        isLoadingProfileData: false,
      })
    }

    const applyTaskStatusResult = (data: TaskOut): boolean => {
      if (
        !canCommitProfileWrite(queryClient, profileId, {
          live: live.current,
          taskId,
        })
      ) {
        return true
      }

      const status = data?.status
      if (status === "processing") return false

      if (status === "completed") {
        const newProfileName = (data as { profile_name?: string | null })
          ?.profile_name
        const hold = isTourProcessingHold(profileId)

        let targetProfileId = profileId
        if (typeof newProfileName === "string" && newProfileName !== "") {
          targetProfileId = adoptRenamedProfile(queryClient, {
            profileId,
            newProfileName,
            setSelectedProfileId,
            completeStatus: hold ? "processing" : "success",
            preserveTitle: hold,
          })
          if (hold) {
            if (targetProfileId !== profileId) {
              noteTourHoldRenamed(profileId, targetProfileId)
            }
            noteTourHoldPendingName(targetProfileId, newProfileName)
          }
        } else {
          patchProfileMeta(queryClient, profileId, {
            status: hold ? "processing" : "success",
            taskId: undefined,
            isCreating: false,
            isInDatabase: true,
            isTrackingStatus: true,
          })
        }

        // Tour hold: keep processing card; reveal loads data later.
        if (!hold) {
          void loadProfileDataForProfile(targetProfileId)
        }
        return true
      }

      if (status === "error") {
        patchProfileMeta(queryClient, profileId, {
          status: "error",
          isCreating: false,
          errorMessage:
            data?.error ||
            "Не удалось сформировать анкету профиля. Повторите попытку.",
          isInDatabase: true,
          isTrackingStatus: true,
        })
        return true
      }

      return false
    }

    const resolveLostTask = async () => {
      stop()
      const result = await settled(api.v1.getProfile(profileId))
      if (!live.current) return
      commitIfTracking(live.current, queryClient, profileId, taskId, () => {
        if (result.ok) {
          const forms =
            (result.value.data as { forms?: unknown[] })?.forms ?? []
          if (forms.length > 0) {
            patchProfileMeta(queryClient, profileId, {
              status: "success",
              taskId: undefined,
              isCreating: false,
            })
            void loadProfileDataForProfile(profileId)
            return
          }
        }
        patchProfileMeta(queryClient, profileId, {
          status: "error",
          taskId: undefined,
          isCreating: false,
          errorMessage:
            "Задача формирования потеряна. Запустите формирование анкеты заново.",
        })
      })
    }

    const pollTaskOnce = async () => {
      const result = await settled(api.v1.getTask(taskId))
      if (!live.current) return
      if (
        !canCommitProfileWrite(queryClient, profileId, {
          live: live.current,
          taskId,
        })
      ) {
        stop()
        return
      }

      if (result.ok) {
        const data = result.value.data
        pollFailures = 0
        if (data?.status === "processing") return
        if (applyTaskStatusResult(data)) stop()
        return
      }

      const e = result.error
      const httpStatus = axios.isAxiosError(e) ? e.response?.status : undefined
      pollFailures += 1

      switch (classifyPollFailure(httpStatus, pollFailures)) {
        case "retry":
          return
        case "lost":
          void resolveLostTask()
          return
        case "give_up":
          stop()
          commitIfTracking(live.current, queryClient, profileId, taskId, () => {
            patchProfileMeta(queryClient, profileId, {
              status: "error",
              isCreating: false,
              errorMessage: extractErrorMessage(
                e,
                "Не удалось проверить статус формирования анкеты. Обновите страницу.",
              ),
            })
          })
      }
    }

    const openStream = () => {
      let closed = false
      let source: EventSource | null = null
      let retryTimer: ReturnType<typeof setTimeout> | undefined
      let attempt = 0
      const baseUrl = withTrailingSlash(apiBaseUrl())

      const disconnect = () => {
        source?.close()
        source = null
      }

      const scheduleReconnect = () => {
        if (closed || !live.current) return
        disconnect()
        attempt += 1
        const delay = nextRetryDelay(attempt)
        if (delay === null) return
        retryTimer = setTimeout(connect, delay)
      }

      const connect = () => {
        if (closed || !live.current) return
        const es = new EventSource(`${baseUrl}v1/tasks/${taskId}/events`, {
          withCredentials: true,
        })
        source = es
        es.onmessage = (event) => {
          // Guard post-unmount onmessage (closed alone used to only suppress reconnects).
          if (closed || !live.current) return
          let task: TaskOut
          try {
            task = JSON.parse(event.data) as TaskOut
          } catch {
            scheduleReconnect()
            return
          }
          attempt = 0
          if (applyTaskStatusResult(task)) stop()
        }
        es.onerror = () => {
          scheduleReconnect()
        }
      }

      connect()
      return () => {
        closed = true
        if (retryTimer !== undefined) clearTimeout(retryTimer)
        disconnect()
      }
    }

    streamClose = openStream()
    pollInterval = setInterval(() => {
      void pollTaskOnce()
    }, TASK_POLL_INTERVAL_MS)
    void pollTaskOnce()

    return () => {
      stop()
    }
  }, [profileId, queryClient, setSelectedProfileId, taskId])
}
